/**
 * PrismLeviathan.ts
 * World 4 boss — Crystal Tide Ruins. Serpentine crystal eel with a
 * reflected-beam attack and a post-fire vulnerability window.
 *
 * Behaviour: patrols horizontally across the arena with a gentle
 * vertical bob; periodically pauses to telegraph a reflected beam,
 * fires the beam, then briefly hangs exposed while it re-coils. Crystal
 * scales render shard/stomp hits as `BLOCKED` outside the post-fire
 * `RECOVERING` window — the player can only punish in that gap.
 *
 * State machine:
 *   SWIMMING → TELL → RECOVERING → SWIMMING (loop)
 *                                → DEAD (when HP → 0 mid-RECOVERING)
 *
 * The reflected beam projectile itself is deferred to the level combat
 * layer via a single-tick `fireRequested` pulse on TELL exit (same
 * pattern as `SparkImp`, `TideMirror`, `KingRivet`). The Leviathan is
 * autonomous — `nova` is accepted for the `Enemy` contract but unused.
 *
 * Rules (AGENTS.md):
 *   - No React / Skia / async imports.
 *   - Extends BaseEntity.
 *   - AI state transitions logged at DEBUG level.
 *
 * Phase 4 — World 4 boss greybox stub. Wired into levels when W4 ships.
 * See ROADMAP.md Phase 4 (`PrismLeviathan.ts`).
 *
 * @module PrismLeviathan
 */

import { BaseEntity } from '../BaseEntity';
import type { LevelRuntime, NovaRuntime } from '../../engine/gameState';
import Logger from '../../utils/logger';

export type PrismLeviathanState = 'SWIMMING' | 'TELL' | 'RECOVERING' | 'DEAD';

/**
 * Projectile-impact result. `BLOCKED` outside the RECOVERING window
 * (scales up); `HURT` only during RECOVERING.
 */
export type PrismLeviathanHitResult = 'BLOCKED' | 'HURT' | 'IGNORED';

/** Boss hitbox dimensions in logical pixels — long, narrow eel silhouette. */
export const PRISM_LEVIATHAN_WIDTH = 56;
export const PRISM_LEVIATHAN_HEIGHT = 28;
/** Greybox render colour — prism violet. */
export const PRISM_LEVIATHAN_COLOR = '#8aa3d9';

/** Single-pool HP (matches ROADMAP spec). */
export const PRISM_LEVIATHAN_MAX_HP = 10;

/** Horizontal patrol speed (px/s) while SWIMMING. */
const SWIM_SPEED = 120;
/** Vertical bob amplitude and period — applied in every live state. */
const BOB_AMPLITUDE = 30;
const BOB_PERIOD = 2.0;
const BOB_OMEGA = (2 * Math.PI) / BOB_PERIOD;

/** Time spent swimming between beam tells. */
const SWIM_DURATION = 3.0;
/** Telegraph length. */
const TELL_DURATION = 1.0;
/** Post-fire vulnerability window. */
const RECOVER_DURATION = 1.2;

/**
 * PrismLeviathan — World 4 boss (Crystal Tide Ruins).
 *
 * Spawn with `new PrismLeviathan(x, y, patrolHalfWidth)`. `(x, y)` is
 * the hitbox top-left, matching the `BaseEntity` contract and every
 * other enemy in the codebase — author the Tiled object at the desired
 * top-left and the loader passes those coordinates through unchanged.
 * The patrol/bob anchor is derived internally as the hitbox centre
 * `(x + W/2, y + H/2)`, so the eel patrols between centre positions
 * `centerX ± patrolHalfWidth` and bobs vertically around `centerY`.
 * Call `update(dt, level, nova?)` each fixed tick. Read `fireRequested`
 * for the single-tick beam pulse, `dead` to know when to remove, and
 * `hp` for HUD purposes.
 */
export class PrismLeviathan extends BaseEntity {
  public readonly entityType = 'PrismLeviathan';
  public readonly color = PRISM_LEVIATHAN_COLOR;
  public readonly maxHp = PRISM_LEVIATHAN_MAX_HP;
  public state: PrismLeviathanState = 'SWIMMING';

  /**
   * True once dead. PrismLeviathan has no intermediate death animation — the
   * fatal hit sets `DEAD` directly. See `Enemy.isDying`.
   */
  public get isDying(): boolean {
    return this.state === 'DEAD';
  }
  /** Remaining HP. Decremented only by hits landed during RECOVERING. */
  public hp = PRISM_LEVIATHAN_MAX_HP;
  /** True when facing right (derived from current horizontal direction). */
  public facingRight = true;
  /** Single-tick pulse — true on the tick the Leviathan exits TELL. */
  public fireRequested = false;
  /** Patrol anchor and bob centre — hitbox centre derived from spawn. */
  private readonly _centerX: number;
  private readonly _centerY: number;
  /** Half-width of the horizontal patrol range (centre-to-edge). */
  private readonly _patrolHalfWidth: number;
  /** Horizontal velocity sign — +1 right, −1 left. Pauses (0) outside SWIMMING. */
  private _swimDir: 1 | -1 = 1;
  /** Timers. */
  private _swimTimer = SWIM_DURATION;
  private _tellTimer = 0;
  private _recoverTimer = 0;
  /** Phase accumulator for the vertical bob. */
  private _bobPhase = 0;

  /**
   * @param x               Hitbox top-left X (BaseEntity contract).
   * @param y               Hitbox top-left Y (BaseEntity contract).
   * @param patrolHalfWidth Horizontal patrol radius around the centre.
   */
  constructor(x: number, y: number, patrolHalfWidth: number) {
    super(x, y, PRISM_LEVIATHAN_WIDTH, PRISM_LEVIATHAN_HEIGHT);
    // DECISION: Bob/patrol anchor is the hitbox CENTRE — derive once from
    // the top-left spawn so internal math stays symmetric around the
    // centre while `this.x` / `this.y` continue to expose the top-left
    // every collider and renderer downstream expects.
    this._centerX = x + PRISM_LEVIATHAN_WIDTH / 2;
    this._centerY = y + PRISM_LEVIATHAN_HEIGHT / 2;
    this._patrolHalfWidth = Math.max(0, patrolHalfWidth);
  }

  /**
   * Advance the boss by one fixed tick.
   *
   * @param dt    Fixed-step delta in seconds.
   * @param _level Level geometry — unused (aerial patrol, no collision).
   * @param _nova  Nova runtime — unused (autonomous boss).
   */
  public update(dt: number, _level: LevelRuntime, _nova?: NovaRuntime): void {
    // DECISION: Clear the pulse at the top of every tick; _exitTell()
    // re-asserts it the same tick it fires for a single-tick pulse
    // matching SparkImp / TideMirror / KingRivet.
    this.fireRequested = false;
    if (this.state === 'DEAD') return;

    this._updateBob(dt);

    switch (this.state) {
      case 'SWIMMING':
        this._updateSwim(dt);
        this._swimTimer -= dt;
        if (this._swimTimer <= 0) this._enterTell();
        break;
      case 'TELL':
        // Paused — no horizontal motion. Bob continues.
        this._tellTimer -= dt;
        if (this._tellTimer <= 0) this._exitTell();
        break;
      case 'RECOVERING':
        // Paused, vulnerable. Bob continues.
        this._recoverTimer -= dt;
        if (this._recoverTimer <= 0) this._enterSwimming();
        break;
    }
  }

  /**
   * Drive the sinusoidal vertical bob — the hitbox CENTRE oscillates
   * around `_centerY`, which means the top-left `this.y` oscillates
   * around `_centerY − H/2` (i.e. the spawn y). Applies in every live
   * state — the Leviathan never holds perfectly still.
   */
  private _updateBob(dt: number): void {
    this._bobPhase += dt;
    this.y =
      this._centerY -
      PRISM_LEVIATHAN_HEIGHT / 2 +
      Math.sin(this._bobPhase * BOB_OMEGA) * BOB_AMPLITUDE;
  }

  /**
   * Drive horizontal patrol. The hitbox CENTRE bounces at
   * `_patrolHalfWidth` either side of `_centerX`; the top-left bounds on
   * `this.x` are shifted left by `W/2` accordingly. Flips `_swimDir` and
   * `facingRight` together. Degenerate zero-width patrol pins the boss
   * without flipping facing.
   */
  private _updateSwim(dt: number): void {
    // DECISION: Guard zero-width patrol — leftBound === rightBound there,
    // and the symmetric bounce branches would flip facing every tick as
    // sub-pixel motion crossed the single point. Pin x and vx instead.
    if (this._patrolHalfWidth <= 0) {
      this.x = this._centerX - PRISM_LEVIATHAN_WIDTH / 2;
      this.vx = 0;
      return;
    }
    this.vx = this._swimDir * SWIM_SPEED;
    this.x += this.vx * dt;
    // Convert centre-space patrol bounds to top-left bounds on this.x.
    const halfW = PRISM_LEVIATHAN_WIDTH / 2;
    const leftBound = this._centerX - this._patrolHalfWidth - halfW;
    const rightBound = this._centerX + this._patrolHalfWidth - halfW;
    if (this.x >= rightBound && this._swimDir === 1) {
      this.x = rightBound;
      this._swimDir = -1;
      this.facingRight = false;
    } else if (this.x <= leftBound && this._swimDir === -1) {
      this.x = leftBound;
      this._swimDir = 1;
      this.facingRight = true;
    }
  }

  private _enterTell(): void {
    this.state = 'TELL';
    this._tellTimer = TELL_DURATION;
    this.vx = 0;
    Logger.debug('PrismLeviathan', 'Beam tell → telegraphing reflected beam');
  }

  private _exitTell(): void {
    this.state = 'RECOVERING';
    this._recoverTimer = RECOVER_DURATION;
    this.fireRequested = true; // single-tick pulse on tell exit (beam fires)
    Logger.debug('PrismLeviathan', 'Tell complete → beam fires, RECOVERING');
  }

  private _enterSwimming(): void {
    this.state = 'SWIMMING';
    this._swimTimer = SWIM_DURATION;
    Logger.debug('PrismLeviathan', 'Recovered → SWIMMING');
  }

  /**
   * Apply one projectile hit.
   * @returns `'BLOCKED'` outside RECOVERING (scales up); `'HURT'` while
   *          RECOVERING; `'IGNORED'` once DEAD.
   */
  public applyShardHit(): PrismLeviathanHitResult {
    if (this.state === 'DEAD') return 'IGNORED';
    if (this.state !== 'RECOVERING') return 'BLOCKED';
    this._takeDamage();
    return 'HURT';
  }

  /**
   * Apply one stomp hit. Scales repel stomps outside RECOVERING.
   * @returns `true` when the stomp connected; `false` otherwise.
   */
  public applyStomp(): boolean {
    if (this.state === 'DEAD') return false;
    if (this.state !== 'RECOVERING') return false;
    this._takeDamage();
    return true;
  }

  /**
   * Shared `Enemy` contract adapter (see gameStateTypes.ts).
   * @param fromStomp `true` for stomp contact, `false`/omitted for shards.
   * @returns `true` if this hit dealt damage; `false` if blocked or ignored.
   */
  public applyHit(fromStomp = false): boolean {
    if (fromStomp) return this.applyStomp();
    return this.applyShardHit() === 'HURT';
  }

  /**
   * Deduct one HP and route to DEAD when the pool empties. Called only
   * from a successful RECOVERING-state hit.
   */
  private _takeDamage(): void {
    this.hp -= 1;
    Logger.debug('PrismLeviathan', `Hit landed — HP ${this.hp}/${this.maxHp}`);
    if (this.hp <= 0) {
      this.state = 'DEAD';
      this.dead = true;
      Logger.debug('PrismLeviathan', 'Defeated');
    }
  }
}
